import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { authEventBus, onStaleDataRefetchKey, refetchKeys } from '@peanuds/app-shell';
import { useApiRequest } from '@peanuds/composables';
import {
  generateFingerprint,
  type AuthenticatedOptions,
  type CreateAccountData,
  type GetAccountDepositInformationData,
  type GetAccountDepositInformationResponseDtoOutput,
  type IdempotencySubmission,
  type ListAccountsData,
  peanudsApi,
  type ListAccountsResponseDtoOutput,
  type Options,
  type SetAccountAliasData,
  withAuthenticatedHeaders,
} from '@peanuds/shared';

import { withStoreIdempotencyHeaders } from '../idempotency-submission';
import type {
  ListAccount,
  UserAccount,
  UserAccountBankDetails,
  UserAccountState,
} from './user-accounts.types';

const SUPPORTED_IBAN_CURRENCIES = new Set(['EUR', 'USD', 'GBP']);
let hasRegisteredAccountRefreshListener = false;

const getFirstNonEmptyValue = (...values: Array<string | undefined>) => {
  return values.find((value) => typeof value === 'string' && value.trim().length > 0);
};

const getRoutingCodeValue = (
  routingCodes: Record<string, unknown> | undefined | null,
  key: string,
) => {
  if (!routingCodes) return '';
  const value = routingCodes[key];
  return typeof value === 'string' ? value : '';
};

const accountStatusToStateMap: Record<
  NonNullable<ListAccount['status']> | 'unknown',
  UserAccountState
> = {
  active: 'ACTIVE',
  pending: 'BLOCKED',
  inactive: 'BLOCKED',
  suspended: 'BLOCKED',
  closed: 'DESTROYED',
  declined: 'DESTROYED',
  unknown: 'BLOCKED',
};

const mapListAccountToUserAccount = (account: ListAccount): UserAccount => {
  const resolvedAvailableBalance = account.availableBalance ?? 0;
  const resolvedStatus = account.status ?? 'unknown';
  const resolvedMainAccountId =
    typeof account.mainAccountId === 'string' ? account.mainAccountId : null;

  return {
    ...account,
    id: account.id,
    availableBalance: resolvedAvailableBalance,
    mainAccountId: resolvedMainAccountId,
    status: resolvedStatus,
    profileId: account.clientId,
    tag: account.alias,
    friendlyName:
      getFirstNonEmptyValue(account.alias, account.accountHolderName, account.ledgerNumber) ?? '',
    currency: account.currency,
    iban: account.iban,
    routingCodes: account.routingCodes,
    balances: {
      availableBalance: resolvedAvailableBalance,
      actualBalance: resolvedAvailableBalance,
    },
    state: {
      state: accountStatusToStateMap[resolvedStatus],
    },
    creationTimestamp: 0,
  };
};

export const useUserAccountsStore = defineStore('userAccounts', () => {
  const accountsResponse = ref<ListAccountsResponseDtoOutput | null>(null);
  const accountDepositInformationById = ref<
    Record<string, GetAccountDepositInformationResponseDtoOutput>
  >({});
  const hasLoadedAccounts = ref(false);
  let pendingAccountsRequest: Promise<UserAccount[]> | null = null;
  const pendingDepositInformationRequests: Record<
    string,
    Promise<GetAccountDepositInformationResponseDtoOutput | undefined>
  > = {};
  const activeAccountId = ref<string | null>(null);
  const selectedAccountIds = ref<string[]>([]);
  const lastCreateAccountSubmission = ref<IdempotencySubmission | null>(null);

  const {
    execute: executeFetchAccounts,
    isLoading,
    error,
    reset: resetFetchAccountsState,
    invalidate: invalidateFetchAccounts,
  } = useApiRequest({
    request: async () =>
      peanudsApi.listAccounts(await withAuthenticatedHeaders<ListAccountsData>({})),
    concurrency: {
      strategy: 'latest-wins',
    },
  });

  const {
    execute: executeFetchAccountDepositInformation,
    reset: resetFetchAccountDepositInformationState,
    invalidate: invalidateFetchAccountDepositInformation,
  } = useApiRequest({
    request: async (accountId: string) =>
      peanudsApi.getAccountDepositInformation(
        await withAuthenticatedHeaders<GetAccountDepositInformationData>({
          path: { id: accountId },
        }),
      ),
    concurrency: {
      strategy: 'parallel',
    },
  });

  const {
    execute: executeCreateAccount,
    data: createAccountResult,
    isLoading: isCreateAccountLoading,
    error: createAccountError,
  } = useApiRequest({
    request: (options: Options<CreateAccountData>) => peanudsApi.createAccount(options),
  });

  const { execute: executeSetAccountAlias, isLoading: isSetAccountAliasLoading } = useApiRequest({
    request: (options: Options<SetAccountAliasData>) => peanudsApi.setAccountAlias(options),
  });

  const accounts = computed<UserAccount[]>(() => {
    const source = accountsResponse.value?.data ?? [];
    return source.map(mapListAccountToUserAccount);
  });

  const fetchAccounts = async ({ force = false }: { force?: boolean } = {}) => {
    if (pendingAccountsRequest && !force) {
      return pendingAccountsRequest;
    }

    const request = (async () => {
      const response = await executeFetchAccounts();

      if (!response) {
        return accounts.value;
      }

      accountsResponse.value = response;
      hasLoadedAccounts.value = true;
      ensureValidActiveAccount();
      ensureValidSelectedAccountIds();

      return accounts.value;
    })();

    pendingAccountsRequest = request;

    try {
      return await request;
    } finally {
      if (pendingAccountsRequest === request) {
        pendingAccountsRequest = null;
      }
    }
  };

  const ensureAccountsLoaded = async () => {
    if (hasLoadedAccounts.value) {
      return accounts.value;
    }

    return (await fetchAccounts()) ?? accounts.value;
  };

  const refreshAccounts = async () => {
    const result = await fetchAccounts({ force: true });
    ensureValidActiveAccount();
    ensureValidSelectedAccountIds();
    return result ?? accounts.value;
  };

  const createAccount = async (input: { currency: 'EUR'; alias?: string }) => {
    const body = {
      currency: input.currency,
      ...(input.alias ? { alias: input.alias } : {}),
    };
    const response = await executeCreateAccount(
      withStoreIdempotencyHeaders<AuthenticatedOptions<CreateAccountData>>(
        lastCreateAccountSubmission,
        { body },
        generateFingerprint(body),
      ),
    );

    if (response?.data?.account?.id) {
      void refreshAccounts();
    }

    return response;
  };

  const setAccountAlias = async (accountId: string, alias: string): Promise<boolean> => {
    const response = await executeSetAccountAlias({
      path: { id: accountId },
      body: { alias },
    });

    if (response === undefined) {
      return false;
    }

    await refreshAccounts();
    return true;
  };

  const ensureAccountDepositInformation = async (accountId?: string) => {
    if (!accountId) {
      return;
    }

    const cachedDepositInformation = accountDepositInformationById.value[accountId];
    if (cachedDepositInformation) {
      return cachedDepositInformation;
    }

    const pendingRequest = pendingDepositInformationRequests[accountId];
    if (pendingRequest) {
      return pendingRequest;
    }

    const request = (async () => {
      const response = await executeFetchAccountDepositInformation(accountId);

      if (!response) {
        return;
      }

      accountDepositInformationById.value = {
        ...accountDepositInformationById.value,
        [accountId]: response,
      };

      return response;
    })();

    pendingDepositInformationRequests[accountId] = request;

    try {
      return await request;
    } finally {
      if (pendingDepositInformationRequests[accountId] === request) {
        delete pendingDepositInformationRequests[accountId];
      }
    }
  };

  const activeAccount = computed(() => {
    if (activeAccountId.value === null || activeAccountId.value === undefined) return undefined;
    return accounts.value.find((a) => a.id === activeAccountId.value);
  });

  const activeAccountBalance = computed(() => {
    const balance = activeAccount.value?.balances.availableBalance;
    return typeof balance === 'number' ? balance : null;
  });

  const activeAccountBankDetails = computed(() => {
    return getAccountBankDetails(activeAccountId.value ?? undefined);
  });

  const setActiveAccountId = (accountId: string | null) => {
    if (accountId === null) {
      activeAccountId.value = null;
      return;
    }

    const exists = accounts.value.some((a) => a.id === accountId);
    if (exists) {
      activeAccountId.value = accountId;
    }
  };

  const getValidSelectedAccountIds = (accountIds: string[]) => {
    const validAccountIds = new Set(accounts.value.map((account) => account.id));

    return accountIds.filter((accountId, index) => {
      return validAccountIds.has(accountId) && accountIds.indexOf(accountId) === index;
    });
  };

  const getFallbackSelectedAccountIds = () => {
    if (accounts.value.length === 0) {
      return [];
    }

    const fallbackAccountId = accounts.value[0]?.id;

    return fallbackAccountId ? [fallbackAccountId] : [];
  };

  const setSelectedAccountIds = (accountIds: string[]) => {
    const validSelectedAccountIds = getValidSelectedAccountIds(accountIds);

    if (validSelectedAccountIds.length > 0 || accounts.value.length === 0) {
      selectedAccountIds.value = validSelectedAccountIds;
      return;
    }

    selectedAccountIds.value = getFallbackSelectedAccountIds();
  };

  const ensureValidActiveAccount = () => {
    if (accounts.value.length === 0) {
      activeAccountId.value = null;
      return;
    }

    const currentValid =
      activeAccountId.value !== null && accounts.value.some((a) => a.id === activeAccountId.value);

    if (!currentValid) {
      activeAccountId.value = accounts.value[0]?.id ?? null;
    }
  };

  const ensureValidSelectedAccountIds = () => {
    selectedAccountIds.value = getValidSelectedAccountIds(selectedAccountIds.value);

    if (selectedAccountIds.value.length > 0 || accounts.value.length === 0) {
      return;
    }

    selectedAccountIds.value = getFallbackSelectedAccountIds();
  };

  const ibanAccounts = computed(() => {
    return accounts.value.filter((account) => SUPPORTED_IBAN_CURRENCIES.has(account.currency));
  });
  const supportedIbanCurrenciesCount = computed(() => {
    return new Set(ibanAccounts.value.map((account) => account.currency)).size;
  });

  const getAccountById = (accountId: string) => {
    if (!accountId) {
      return;
    }

    return accounts.value.find(({ id }) => id === accountId);
  };

  const getAccountBankDetails = (accountId?: string): UserAccountBankDetails | undefined => {
    if (!accountId) {
      return;
    }

    const account = getAccountById(accountId);
    const depositInformation = accountDepositInformationById.value[accountId];

    if (!account || (!account.iban && !account.accountNumber && !depositInformation)) {
      return;
    }

    const sepa = depositInformation?.sepa;
    const bic =
      getRoutingCodeValue(sepa?.routingCodes, 'bic') ||
      getRoutingCodeValue(account.routingCodes, 'bic');
    const swiftCode = getRoutingCodeValue(account.routingCodes, 'swift') || bic;
    const receiver = getFirstNonEmptyValue(
      sepa?.accountHolderName ?? undefined,
      account.accountHolderName,
    );

    return {
      iban: sepa?.iban ?? account.iban ?? '',
      accountNumber: account.accountNumber ?? '',
      bic,
      swiftCode,
      receiver: receiver?.trim() ?? '',
      bankName: sepa?.bankName ?? '',
      bankAddress: sepa?.bankAddress ?? '',
      depositInformation,
    };
  };

  const balances = computed(() => {
    return accounts.value.map((account) => account.balances?.availableBalance);
  });

  const totalBalance = computed(() => {
    return balances.value.reduce((acc, curr) => acc + curr, 0);
  });

  authEventBus.subscribe('logout', () => {
    pendingAccountsRequest = null;
    Object.keys(pendingDepositInformationRequests).forEach((accountId) => {
      delete pendingDepositInformationRequests[accountId];
    });
    invalidateFetchAccounts();
    invalidateFetchAccountDepositInformation();
    resetFetchAccountsState();
    resetFetchAccountDepositInformationState();
    activeAccountId.value = null;
    selectedAccountIds.value = [];
    lastCreateAccountSubmission.value = null;
    useUserAccountsStore().$reset();
  });

  if (!hasRegisteredAccountRefreshListener) {
    hasRegisteredAccountRefreshListener = true;

    const refreshAccountsFromStaleData = () => {
      void useUserAccountsStore().refreshAccounts();
    };

    onStaleDataRefetchKey(refetchKeys.ACCOUNTS, refreshAccountsFromStaleData);
    onStaleDataRefetchKey(refetchKeys.SDD_CREDITOR_ID_STATUS, refreshAccountsFromStaleData);
    onStaleDataRefetchKey(refetchKeys.INCOMING_TRANSFER, refreshAccountsFromStaleData);
    onStaleDataRefetchKey(refetchKeys.OUTGOING_TRANSFERS, refreshAccountsFromStaleData);
    onStaleDataRefetchKey(refetchKeys.GENERIC_TRANSACTION, refreshAccountsFromStaleData);
    onStaleDataRefetchKey(refetchKeys.EXCHANGE, refreshAccountsFromStaleData);
  }

  return {
    accounts,
    accountsResponse,
    accountDepositInformationById,
    ibanAccounts,
    supportedIbanCurrenciesCount,
    hasLoadedAccounts,
    isLoading,
    error,
    createAccountResult,
    isCreateAccountLoading,
    createAccountError,
    isSetAccountAliasLoading,
    activeAccountId,
    selectedAccountIds,
    activeAccount,
    activeAccountBalance,
    activeAccountBankDetails,
    setActiveAccountId,
    setSelectedAccountIds,
    ensureValidActiveAccount,
    ensureValidSelectedAccountIds,
    balances,
    totalBalance,
    fetchAccounts,
    ensureAccountsLoaded,
    refreshAccounts,
    createAccount,
    setAccountAlias,
    ensureAccountDepositInformation,
    getAccountById,
    getAccountBankDetails,
  };
});
